import React from 'react';
import { motion } from 'motion/react';
import { FileText, ArrowLeft, Mail, Phone, MapPin, Scale, CreditCard, XCircle, RotateCcw, AlertTriangle, Shield } from 'lucide-react';

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 220, damping: 22 } },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <motion.section variants={sectionVariants} className="mb-8">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.2)' }}>
          <Icon size={15} className="text-emerald-400" />
        </div>
        <h2 className="text-base font-black text-white tracking-tight">{title}</h2>
      </div>
      <div className="text-[13px] text-white/55 leading-relaxed pl-[42px]">
        {children}
      </div>
    </motion.section>
  );
}

export default function TermsOfService() {
  return (
    <div
      className="min-h-screen w-full font-sans flex flex-col"
      style={{ background: 'radial-gradient(ellipse at 60% 0%, #0a1f1a 0%, #040d09 50%, #020807 100%)' }}
    >
      {/* Ambient orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-15" style={{ background: 'radial-gradient(circle, #059669 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <div className="absolute top-1/3 -left-48 w-[500px] h-[500px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #059669 0%, transparent 60%)', filter: 'blur(80px)' }} />
      </div>

      {/* Sticky Header */}
      <div className="sticky top-0 z-30 backdrop-blur-xl border-b border-white/5 px-4 py-3 flex items-center shrink-0" style={{ background: 'rgba(4, 13, 9, 0.85)' }}>
        <motion.button
          whileTap={{ scale: 0.94 }}
          onClick={() => window.history.back()}
          className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors text-xs font-bold"
        >
          <ArrowLeft size={15} />
          Back to Home
        </motion.button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pb-16">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="w-full max-w-3xl mx-auto px-5 sm:px-6 pt-8 pb-8"
        >
          {/* Page Header */}
          <motion.div variants={sectionVariants} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative">
                <div className="absolute inset-0 rounded-2xl blur-md" style={{ background: 'rgba(16,185,129,0.35)' }} />
                <div className="relative w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: '#064e3b', border: '1px solid #05966980' }}>
                  <FileText size={22} className="text-emerald-400" />
                </div>
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">Terms of Service</h1>
                <p className="text-xs text-white/40 mt-0.5">Kangavena Cloud · Sri Lanka</p>
              </div>
            </div>
          </motion.div>

          {/* ═══════════ TERMS OF SERVICE ═══════════ */}

          <Section icon={Scale} title="Acceptance of Terms">
            <p>
              By accessing or using the Kangavena Cloud platform ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service. These terms constitute a legally binding agreement between you ("User," "you," or "your") and Kangavena Cloud, operating in Sri Lanka.
            </p>
          </Section>

          <Section icon={FileText} title="Description of Service">
            <p>
              Kangavena Cloud provides a cloud-based point-of-sale (POS) platform designed for retail businesses, hotels, and automotive garages across Sri Lanka. The platform includes features such as inventory management, sales tracking, voice billing, analytics, multi-store management, and an AI assistant (Luna). The Service operates on a revenue-share model — there are no upfront subscription fees. Instead, Kangavena charges a small percentage of your store's monthly net profit generated through the POS system.
            </p>
          </Section>

          <Section icon={Shield} title="Account Responsibilities">
            <p className="mb-3">By creating an account, you agree to:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>Provide <strong className="text-white/70">accurate and complete</strong> business and personal information during registration.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Keep your login credentials secure</strong> and confidential. You are responsible for all activity that occurs under your account.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Notify us immediately</strong> if you suspect unauthorized access to your account.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>Manage employee and cashier accounts with appropriate role-based access permissions.</span>
              </li>
            </ul>
          </Section>

          <Section icon={AlertTriangle} title="Acceptable Use">
            <p className="mb-3">You agree to use the Service only for <strong className="text-white/70">lawful business purposes</strong>. You must not:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>Use the Service for any illegal activity, including money laundering, fraud, or tax evasion.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>Attempt to hack, reverse-engineer, or exploit the Service or its infrastructure.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>Send spam, phishing attempts, or distribute malware through the platform.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>Exceed reasonable usage limits or attempt to overload the platform's infrastructure.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>Share access credentials with unauthorized third parties.</span>
              </li>
            </ul>
          </Section>

          <Section icon={Shield} title="Intellectual Property">
            <p>
              All intellectual property rights in the Service, including but not limited to the Kangavena brand name, logos, software code, user interface designs, AI models (Luna), documentation, and all related materials are the exclusive property of Kangavena Cloud. You are granted a limited, non-exclusive, non-transferable license to use the Service for your business operations during your active use of the Service. You may not copy, modify, distribute, or create derivative works from any part of the Service.
            </p>
          </Section>

          <Section icon={CreditCard} title="Billing & Payment">
            <p className="mb-3">Kangavena Cloud operates on a <strong className="text-white/70">revenue-share model</strong> with no upfront subscription fees:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Revenue Share:</strong> Kangavena charges between <strong className="text-white/70">1% to 2.5%</strong> of your store's monthly net profit generated through the POS system. The exact percentage is agreed upon during onboarding.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Monthly Billing Cycle:</strong> At the end of each calendar month, Kangavena will calculate your net profit and send you a notice with the available balance due. Payment is expected within the following month.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Late Payment & System Lock:</strong> If payment is not received by the end of the second month (60 days from the original billing notice), Kangavena reserves the right to <strong className="text-white/70">lock down your POS system</strong>, restricting access to all features until the outstanding balance is settled in full.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>All fees are calculated and quoted in Sri Lankan Rupees (LKR).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>We reserve the right to adjust the revenue-share percentage with 30 days' advance written notice.</span>
              </li>
            </ul>
          </Section>

          <Section icon={XCircle} title="Termination & System Lock">
            <p className="mb-3">
              Either party may terminate this agreement:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">By You:</strong> You may stop using the Service at any time by contacting us. Any outstanding revenue-share balance must be settled before your account is closed.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">By Us:</strong> We may terminate your access if you violate these terms, with reasonable notice.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">System Lock for Non-Payment:</strong> If your outstanding revenue-share balance is not paid within 2 months (60 days) from the billing notice date, Kangavena will <strong className="text-white/70">lock your POS system</strong>, disabling all operations until the balance is fully cleared. You will receive at least one reminder notice before the lock is applied.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Data After Termination:</strong> Upon termination, we will retain your data for 30 days to allow for data export. After this period, your data will be permanently deleted from our active systems. Backup copies may be purged within 90 days.</span>
              </li>
            </ul>
          </Section>

          <Section icon={AlertTriangle} title="Limitation of Liability">
            <p className="mb-3">
              The Service is provided <strong className="text-white/70">"as is" and "as available"</strong> without warranties of any kind, either express or implied. While we strive for 99.9% uptime, we do not guarantee uninterrupted service.
            </p>
            <p className="mb-3">
              Kangavena Cloud shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service, including but not limited to loss of profits, data, business opportunities, or goodwill.
            </p>
            <p>
              Our total liability for any claim arising from or related to the Service shall not exceed the amount you paid for the Service in the 12 months preceding the claim.
            </p>
          </Section>

          <Section icon={Scale} title="Governing Law">
            <p>
              These Terms of Service shall be governed by and construed in accordance with the laws of the Democratic Socialist Republic of Sri Lanka. Any disputes arising under or in connection with these terms shall be subject to the exclusive jurisdiction of the courts of Sri Lanka.
            </p>
          </Section>

          {/* Contact */}
          <Section icon={Mail} title="Contact Us">
            <p className="mb-3">
              For any questions about these terms or the refund policy:
            </p>
            <div className="rounded-2xl p-4 mt-2" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
              <div className="space-y-2.5">
                <a href="mailto:sissara.com@gmail.com" className="flex items-center gap-2.5 text-white/60 hover:text-emerald-400 transition-colors">
                  <Mail size={14} className="text-emerald-500" />
                  <span className="text-sm">sissara.com@gmail.com</span>
                </a>
                <a href="https://wa.me/94767114888" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-white/60 hover:text-emerald-400 transition-colors">
                  <Phone size={14} className="text-emerald-500" />
                  <span className="text-sm">076 711 4888 (WhatsApp)</span>
                </a>
                <div className="flex items-center gap-2.5 text-white/60">
                  <MapPin size={14} className="text-emerald-500" />
                  <span className="text-sm">Sri Lanka</span>
                </div>
              </div>
            </div>
          </Section>

          {/* Back to Home */}
          <motion.div variants={sectionVariants} className="text-center pt-4 pb-8">
            <a href="/" className="inline-flex items-center gap-2 text-xs font-bold text-emerald-400/60 hover:text-emerald-400 transition-colors">
              <ArrowLeft size={13} />
              Back to Home
            </a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
